import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { TDWActionType, TDWModuleType, TDWOperationType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { ISlideBaseAppServices } from '@src/slide/application/entry/toc/base/slide-base-app-services.interface';
import { IEditorAppServices } from '@src/slide/application/entry/toc/editor/base/base-editor-app-service.interface';
import { IPictureModelStyle } from '../../Interface/model-props/picture-model-props.interface';
import { IPictureModel } from '../../Interface/picture-model.interface';
import { IBaseImageCrop, IImageCrop } from './interface/image-crop.interface';
import { IShapeModel } from '../../Interface/shape-model.interface';

export const CROP_KEY = 'tailor-pic';
export const CROP_RANGE = [
    'normal', '1-1', '2-3', '3-4', '3-5', '4-5',
    '3-2', '4-3', '5-3', '5-4', '16-9', '16-10',
].map(e => `${CROP_KEY}-${e}`);

export class BaseImageCrop implements IBaseImageCrop {
    private model: IPictureModel | IShapeModel;
    public constructor(model: IPictureModel | IShapeModel) {
        this.model = model;
    }

    /**
     * 裁剪为形状
     * @param type
     */
    public cropWithShape(type = 'parallelogram'): void {
        if (this.model.props.style.bgShape === type) return;
        const graphicInfo = {
            bgShape: type,
            controlValues: [],
        };
            // 重置关键点数据
        this.model.operatingGraphicInfo = { controlValues: [] };
        this.model.getGraphic().updateGraphicInfo(graphicInfo);
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.PC_OBJ,
            action: TDWActionType.IMG_CUT,
        });
    }
}

// 迁移自picture-model.ts
export class ImageCrop extends BaseImageCrop implements IImageCrop {
    private picModel: IPictureModel;
    public constructor(model: IPictureModel) {
        super(model);
        this.picModel = model;
    }

    /**
     * 裁剪或者裁剪为纵横比
     */
    public handleCrop(actionType?: string): void {
        if (!actionType || !CROP_RANGE.includes(actionType)) {
            return;
        }
        if (actionType !== `${CROP_KEY}-normal`) {
            const [ow, oh] = actionType.replace(new RegExp(`${CROP_KEY}-`), '').split('-');
            this.cropWithWH(Number(ow), Number(oh));
        }
        this.getEditingStateManager().updateElementCropState(true);
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.PC_OBJ,
            action: TDWActionType.IMG_CUT,
        });
    }

    // 以ow,oh为比例进行裁剪
    private cropWithWH = (ow: number, oh: number): void => {
        const { width, height } = this.picModel.props.style;
        let { top, left } = this.picModel.props.style;
        let newWidth = width;
        let newHeight = height;

        // 裁剪比例与原图比例，记录以哪边条为准，同时把top, left更新
        const ratioNew = ow / oh;
        const ratioOld = width / height;
        let offsetX = 0;
        let offsetY = 0;
        if (ratioNew > ratioOld) {
            newHeight = width / ratioNew;
            offsetY = ((height - newHeight) >> 1);
            top += offsetY;
        } else {
            newWidth = height * ratioNew;
            offsetX = ((width - newWidth) >> 1);
            left += offsetX;
        }

        // 生成新的裁剪数据srcRect
        let { srcRect } = this.picModel.props.style;
        if (!srcRect) {
            srcRect = {
                b: 0,
                t: 0,
                l: 0,
                r: 0,
            };
        } else {
            let { pl, pr, pt, pb } = this.picModel.getImageSize().getForegroundImageSize({ width, height, srcRect });
            pl += offsetX;
            pr += offsetX;
            pt += offsetY;
            pb += offsetY;
            srcRect.l = pl / (newWidth + pr + pl);
            srcRect.r = pr / (newWidth + pr + pl);
            srcRect.t = pt / (newHeight + pb + pt);
            srcRect.b = pb / (newHeight + pb + pt);
        }

        // 数组组装
        const attrs = [{
            style: {
                top,
                left,
                width: newWidth,
                height: newHeight,
                srcRect: {
                    ...srcRect,
                },
            },
        }];
        this.picModel.selectionService?.currentSlide?.updateElementsProps(attrs, [this.picModel]);
        this.getBorderSketchManager()?.updatePathListWhenSizeChange(
            this.picModel,
            attrs[0] as Partial<IPictureModelStyle>,
        );
    };

    private getEditingStateManager() {
        const appServices = AppServiceProvider.getInstance().getAppServices() as ISlideBaseAppServices;
        return appServices.viewableServiceLayer.editingStateManager;
    }

    private getBorderSketchManager() {
        const appServices = AppServiceProvider.getInstance().getAppServices() as IEditorAppServices;
        return appServices.editableServiceLayer?.borderSketchManager;
    }
}
